import React, { useState } from 'react';

const ContextMenu = ({ x, y, member, allMembers, activeTab, onAction, onClose }) => {
  const [subMode, setSubMode] = useState(null); // 'link-parents' | 'link-children'
  const [selected, setSelected] = useState([]);
  const [linking, setLinking] = useState(false);

  const menuWidth = 250;
  const menuHeight = 380;
  const adjustedX = x + menuWidth > window.innerWidth ? x - menuWidth : x;
  const adjustedY = y + menuHeight > window.innerHeight ? y - menuHeight : y;

  // Show ALL eligible members including already-linked ones (pre-selected)
  const allParentCandidates = (allMembers || []).filter(m =>
    m.memberId !== member.memberId &&
    m.generation === member.generation - 1 &&
    (m.branch === member.branch ||
      m.isCreator ||
      m.memberId === allMembers.find(a => a.isCreator)?.spouseId)
  );

  const allChildCandidates = (allMembers || []).filter(m =>
    m.memberId !== member.memberId &&
    m.generation === member.generation + 1 &&
    (m.branch === member.branch ||
      m.isCreator ||
      m.memberId === allMembers.find(a => a.isCreator)?.spouseId)
  );

  const initialParentIds = member.parentIds || [];
  const initialChildIds = member.childIds || [];

  const enterSubMode = (mode) => {
    setSubMode(mode);
    setLinking(false);
    if (mode === 'link-parents') {
      setSelected([...initialParentIds]);
    } else {
      setSelected([...initialChildIds]);
    }
  };

  const toggleSelect = (memberId) => {
    setSelected(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      }
      if (subMode === 'link-parents' && prev.length >= 2) return prev;
      return [...prev, memberId];
    });
  };

  const handleSave = () => {
    if (linking) return;
    setLinking(true);
    onAction(subMode, member, selected, subMode === 'link-parents' ? initialParentIds : initialChildIds);
  };

  const handleBack = () => {
    setSubMode(null);
    setSelected([]);
    setLinking(false);
  };

  const hasParentCandidates = allParentCandidates.length > 0;
  const hasChildCandidates = allChildCandidates.length > 0;
  const hasLinkedParents = initialParentIds.length > 0;
  const hasLinkedChildren = initialChildIds.length > 0;

  const mainItems = [
    { key: 'add-spouse', label: '+ Add spouse', disabled: !!member.spouseId },
    { key: 'sep0', label: '---' },
    {
      key: 'link-parents',
      label: '👨‍👩‍👦 Link / Delink parents',
      disabled: !hasParentCandidates && !hasLinkedParents,
      dimLabel: (!hasParentCandidates && !hasLinkedParents) ? '(none available)' : null,
    },
    {
      key: 'link-children',
      label: '👶 Link / Delink children',
      disabled: !hasChildCandidates && !hasLinkedChildren,
      dimLabel: (!hasChildCandidates && !hasLinkedChildren) ? '(none available)' : null,
    },
    { key: 'sep1', label: '---' },
    { key: 'voice', label: '🎙 Add voice memory  (Premium)', disabled: true, premium: true },
    { key: 'video', label: '🎬 Add video memory  (Premium)', disabled: true, premium: true },
    { key: 'link', label: '🔗 Link to family tree', disabled: false },
    { key: 'sep2', label: '---' },
    { key: 'delete', label: '🗑 Delete member', disabled: false, danger: true },
  ];

  const candidates = subMode === 'link-parents' ? allParentCandidates : allChildCandidates;
  const subTitle = subMode === 'link-parents'
    ? 'Select / deselect parents (max 2)'
    : 'Select / deselect children';

  const initialSet = subMode === 'link-parents' ? initialParentIds : initialChildIds;
  const hasChanges = JSON.stringify([...selected].sort()) !== JSON.stringify([...initialSet].sort());

  return (
    <div
      style={{ ...styles.menu, left: adjustedX, top: adjustedY }}
      onClick={e => e.stopPropagation()}
    >
      {/* Header */}
      <div style={styles.menuHeader}>
        <span style={styles.memberName}>{member.name}</span>
        {member.relation && <span style={styles.memberRelation}>{member.relation}</span>}
      </div>

      {/* Main menu */}
      {!subMode && (
        <>
          {mainItems.map(item => {
            if (item.label === '---') return <div key={item.key} style={styles.separator} />;
            let itemStyle = { ...styles.item };
            if (item.disabled) itemStyle = { ...itemStyle, ...styles.itemDisabled };
            if (item.premium) itemStyle = { ...itemStyle, ...styles.itemPremium };
            if (item.danger && !item.disabled) itemStyle = { ...itemStyle, ...styles.itemDanger };
            return (
              <div
                key={item.key}
                style={itemStyle}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  if (item.disabled) return;
                  if (item.key === 'link-parents' || item.key === 'link-children') {
                    enterSubMode(item.key);
                  } else {
                    onAction(item.key, member);
                  }
                }}
              >
                {item.label}
                {item.dimLabel && <span style={styles.dimLabel}> {item.dimLabel}</span>}
                {(item.key === 'link-parents' || item.key === 'link-children') && !item.disabled && (
                  <span style={styles.chevron}>›</span>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* Sub-menu — candidate picker with delink support */}
      {subMode && (
        <div>
          <div style={styles.subHeader}>
            <button style={styles.backBtn} onMouseDown={handleBack}>‹ Back</button>
            <span style={styles.subTitle}>{subTitle}</span>
          </div>

          <div style={{
            padding: '6px 14px', fontSize: 10, color: '#9CA3AF',
            background: '#FAFAFA', borderBottom: '1px solid #f0f0f0', fontStyle: 'italic',
          }}>
            ✓ Ticked = currently linked. Untick to delink.
          </div>

          {candidates.length === 0 ? (
            <div style={styles.noCandidates}>No members available in that generation</div>
          ) : (
            <div style={styles.candidateList}>
              {candidates.map(c => {
                const isSelected = selected.includes(c.memberId);
                const isAlreadyLinked = initialSet.includes(c.memberId);
                const isDisabled = subMode === 'link-parents' && selected.length >= 2 && !isSelected;
                return (
                  <div
                    key={c.memberId}
                    style={{
                      ...styles.candidateItem,
                      ...(isSelected ? styles.candidateSelected : {}),
                      ...(isDisabled ? styles.candidateDisabled : {}),
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      if (!isDisabled) toggleSelect(c.memberId);
                    }}
                  >
                    <div style={{
                      ...styles.candidateAvatar,
                      background: c.gender === 'male' ? '#DBEAFE' : '#FCE7F3',
                      color: c.gender === 'male' ? '#1D4ED8' : '#BE185D',
                    }}>
                      {c.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div style={styles.candidateInfo}>
                      <div style={styles.candidateName}>{c.name}</div>
                      <div style={styles.candidateRel}>
                        {c.relation || '—'}
                        {isAlreadyLinked && (
                          <span style={{ color: '#D4A017', marginLeft: 4, fontWeight: 600 }}>
                            · linked
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{
                      ...styles.checkbox,
                      ...(isSelected ? styles.checkboxSelected : {}),
                    }}>
                      {isSelected && '✓'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {candidates.length > 0 && (
            <div style={styles.linkBtnRow}>
              <button
                style={{
                  ...styles.linkBtn,
                  opacity: !hasChanges || linking ? 0.6 : 1,
                  cursor: !hasChanges || linking ? 'not-allowed' : 'pointer',
                }}
                onMouseDown={(e) => { e.stopPropagation(); if (hasChanges) handleSave(); }}
                disabled={!hasChanges || linking}
              >
                {linking ? 'Saving…' : hasChanges ? 'Save Changes' : 'No changes'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hint */}
      {!subMode && (
        <div style={styles.hint}>
          Single click — view &amp; edit · Triple click — toggle deceased 🪔
        </div>
      )}
    </div>
  );
};

const styles = {
  menu: {
    position: 'fixed',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    zIndex: 9999,
    minWidth: '250px',
    maxWidth: '280px',
    overflow: 'hidden',
    fontFamily: "'DM Sans', Arial, sans-serif",
  },
  menuHeader: {
    padding: '10px 14px 8px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  memberName: { fontSize: '13px', fontWeight: '600', color: '#111827' },
  memberRelation: { fontSize: '11px', color: '#9CA3AF' },
  item: { padding: '9px 14px', fontSize: '13px', color: '#111827', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  itemDisabled: { color: '#D1D5DB', cursor: 'default' },
  itemPremium: { color: '#D1D5DB', cursor: 'default', fontSize: '12px' },
  itemDanger: { color: '#DC2626', cursor: 'pointer' },
  dimLabel: { fontSize: '10px', color: '#D1D5DB', marginLeft: 4 },
  chevron: { fontSize: '16px', color: '#9CA3AF' },
  separator: { height: '1px', background: '#f0f0f0', margin: '3px 0' },
  hint: { padding: '7px 14px', fontSize: '10px', color: '#6B7280', borderTop: '1px solid #f0f0f0', background: '#fafafa', fontStyle: 'italic' },
  subHeader: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' },
  backBtn: { background: 'none', border: 'none', fontSize: '16px', color: '#6B7280', cursor: 'pointer', padding: '0 4px', lineHeight: 1 },
  subTitle: { fontSize: '12px', fontWeight: '600', color: '#374151' },
  noCandidates: { padding: '14px', fontSize: '12px', color: '#9CA3AF', textAlign: 'center' },
  candidateList: { maxHeight: '220px', overflowY: 'auto' },
  candidateItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #f9f9f9' },
  candidateSelected: { background: '#FFFBEB' },
  candidateDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  candidateAvatar: { width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 },
  candidateInfo: { flex: 1, minWidth: 0 },
  candidateName: { fontSize: '12px', fontWeight: '600', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  candidateRel: { fontSize: '10px', color: '#9CA3AF' },
  checkbox: { width: 18, height: 18, borderRadius: 4, border: '1.5px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', flexShrink: 0 },
  checkboxSelected: { background: '#D4A017', borderColor: '#D4A017' },
  linkBtnRow: { padding: '10px 14px', borderTop: '1px solid #f0f0f0' },
  linkBtn: { width: '100%', background: '#0e0018', color: '#D4A017', border: 'none', borderRadius: 6, padding: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};

export default ContextMenu;
